import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { AgentSavedDashboardItem } from '@magimp/shared';
import {
  deleteAgentSavedDashboardItem,
  listAgentSavedDashboardItems,
  reorderAgentSavedDashboardItems,
} from '../../api/agent/index.js';
import { useToastStore } from '../../stores/toast.js';
import {
  Button,
  Card,
  EmptyState,
  ErrorState,
  LoadingState,
  ToolbarActions,
} from '../../ui/index.js';
import { ArtifactRenderer } from './artifacts/index.js';

export function AgentSavedDashboardPage() {
  const { t } = useTranslation('agent');
  const showToast = useToastStore((s) => s.show);
  const [items, setItems] = useState<AgentSavedDashboardItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const response = await listAgentSavedDashboardItems();
      setItems(response.items);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const reorder = useCallback(
    async (fromIndex: number, toIndex: number) => {
      const next = [...items];
      const [moved] = next.splice(fromIndex, 1);
      if (moved === undefined) return;
      next.splice(toIndex, 0, moved);
      setItems(next.map((item, index) => ({ ...item, position: index })));
      setBusyId(moved.id);
      try {
        const response = await reorderAgentSavedDashboardItems({
          items: next.map((item, index) => ({ id: item.id, position: index })),
        });
        setItems(response.items);
      } catch {
        showToast({ tone: 'error', text: t('savedDashboard.reorderFailed') });
        await load();
      } finally {
        setBusyId((current) => (current === moved.id ? null : current));
      }
    },
    [items, load, showToast, t],
  );

  const unpin = useCallback(
    async (id: string) => {
      setBusyId(id);
      try {
        await deleteAgentSavedDashboardItem(id);
        setItems((current) => current.filter((item) => item.id !== id));
        showToast({ tone: 'success', text: t('savedDashboard.unpinned') });
      } catch {
        showToast({ tone: 'error', text: t('savedDashboard.unpinFailed') });
      } finally {
        setBusyId((current) => (current === id ? null : current));
      }
    },
    [showToast, t],
  );

  return (
    <div
      data-testid="agent-saved-dashboard-page"
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--s-4)',
        padding: 'var(--s-3)',
        minHeight: 0,
      }}
    >
      <header
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--s-3)',
          flexWrap: 'wrap',
        }}
      >
        <h1 style={{ fontSize: 20, fontWeight: 600, margin: 0, flex: '1 1 auto' }}>
          {t('savedDashboard.title')}
        </h1>
        <ToolbarActions>
          <Button kind="secondary" icon="refresh-cw" onClick={() => void load()} disabled={loading}>
            {t('savedDashboard.refresh')}
          </Button>
        </ToolbarActions>
      </header>

      {loading ? <LoadingState text={t('savedDashboard.loading')} /> : null}
      {!loading && error ? (
        <ErrorState
          title={t('savedDashboard.loadFailed')}
          text={t('savedDashboard.loadFailedText')}
          onRetry={() => void load()}
          retryLabel={t('savedDashboard.retry')}
        />
      ) : null}
      {!loading && !error && items.length === 0 ? (
        <EmptyState
          icon="kanban"
          title={t('savedDashboard.emptyTitle')}
          text={t('savedDashboard.empty')}
        />
      ) : null}
      {!loading && !error && items.length > 0 ? (
        <section
          aria-label={t('savedDashboard.regionLabel')}
          style={{
            display: 'grid',
            gridTemplateColumns: 'minmax(0, 1fr)',
            gap: 'var(--s-3)',
          }}
        >
          {items.map((item, index) => (
            <Card
              key={item.id}
              style={{
                padding: 'var(--s-4)',
                display: 'flex',
                flexDirection: 'column',
                gap: 'var(--s-3)',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  gap: 'var(--s-2)',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                }}
              >
                <h2 style={{ fontSize: 15, fontWeight: 600, margin: 0, flex: '1 1 auto' }}>
                  {item.title}
                </h2>
                <Button
                  kind="ghost"
                  size="sm"
                  icon="arrow-up-down"
                  disabled={index === 0 || busyId === item.id}
                  onClick={() => void reorder(index, index - 1)}
                >
                  {t('savedDashboard.moveUp')}
                </Button>
                <Button
                  kind="ghost"
                  size="sm"
                  icon="arrow-up-down"
                  disabled={index === items.length - 1 || busyId === item.id}
                  onClick={() => void reorder(index, index + 1)}
                >
                  {t('savedDashboard.moveDown')}
                </Button>
                <Button
                  kind="danger"
                  size="sm"
                  icon="trash-2"
                  disabled={busyId === item.id}
                  onClick={() => void unpin(item.id)}
                >
                  {t('savedDashboard.unpin')}
                </Button>
              </div>
              <ArtifactRenderer artifact={item.artifact} />
            </Card>
          ))}
        </section>
      ) : null}
    </div>
  );
}
